/**
 * B-4v2 — the Today HEALTH-SYNC HOME strip (native only).
 *
 * Founder direction 2026-07-17: the H① sleep/calories background sync is
 * invisible and beta users think it's broken. This strip lives where the old
 * readiness/device-connect strip did (the hero meta region) and makes the sync
 * EVIDENT + controllable, following the ReadinessSlot strip grammar/footprint.
 *
 * States (server-authoritative via useHealthSyncStatus):
 *   (a) never-synced / permission missing → whole strip is a "Connect Apple
 *       Health" CTA that triggers the H① permission + pull (syncHealthMetrics).
 *   (b) synced → a quiet one-line "Health synced · today" (or the last-synced
 *       day when stale) with a small manual-refresh affordance (same H① pull).
 *
 * Copy is calm, athlete-language: no shame, no medical language, no raw values.
 * Rendered ONLY when `health_home_v1` is on AND native (the slot wrapper hides
 * it on web); flag OFF the wrapper renders the legacy ReadinessSlot unchanged.
 */
import { useHealthSyncStatus } from '../../hooks/useHealthSyncStatus';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import { syncFreshnessLabel, isSyncStale } from '../../lib/health/healthSyncLabel';
import styles from './HealthHomeStrip.module.css';

/** A2 — `sys.vitals` @13. NOT `kudo.heart`: a kudo is a message between
 *  people, a vital is a measurement. Separate picks, deliberately. */
const HeartGlyph = () => {
  const iconsOn = useIconSystem();
  if (iconsOn) return <Icon name="sys.vitals" size={13} />;
  return (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <path d="M20.8 8.6a5 5 0 0 0-8.8-3.2A5 5 0 0 0 3.2 8.6c0 4.2 5.2 8 8.8 10.8 3.6-2.8 8.8-6.6 8.8-10.8Z" />
  </svg>
  );
};

/** A2 — `sys.sync` @13. */
const RefreshGlyph = () => {
  const iconsOn = useIconSystem();
  if (iconsOn) return <Icon name="sys.sync" size={13} />;
  return (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden>
    <path d="M20 11a8 8 0 1 0-.6 4" />
    <path d="M20 4v5h-5" />
  </svg>
  );
};

export interface HealthHomeStripProps {
  className?: string;
  /**
   * 2.0 Today (W1-TODAY-CARDS, TC-2). The stale/synced chip with the icon
   * refresh button gives way to the status row (`today/v2/StatusRow`), so with
   * `v2` only the never-synced connect CTA renders (D11: it stays on Today
   * until Settings owns the connect). Absent/false = byte-identical to before.
   */
  v2?: boolean;
}

export function HealthHomeStrip({ className, v2 }: HealthHomeStripProps) {
  const { status, loaded, refreshing, refresh } = useHealthSyncStatus();

  // Hold the meta region quiet until the first status resolves — no flash of a
  // "connect" CTA for an already-synced athlete.
  if (!loaded) return null;

  const lastDay = status?.lastSyncedDay ?? null;
  const synced = !!lastDay;

  // ── (a) never-synced / permission missing → connect CTA ──
  if (!synced) {
    return (
      <button
        type="button"
        className={[styles.strip, styles.connect, className].filter(Boolean).join(' ')}
        onClick={refresh}
        disabled={refreshing}
        aria-label="Connect Apple Health to sync sleep and calories"
      >
        <span className={styles.glyph}>
          <HeartGlyph />
        </span>
        <span className={styles.txt}>
          <span className={styles.lbl}>Apple Health</span>
          <span className={styles.hint}>{refreshing ? 'Connecting…' : 'Connect · sync sleep & calories'}</span>
        </span>
      </button>
    );
  }

  // 2.0: the synced states belong to the status row, so nothing renders here.
  if (v2) return null;

  // ── (b) synced → quiet state + manual refresh ──
  // B254 — a sync that keeps running but stops finding anything new (the
  // founder's report: it ran, the newest day never moved) used to read as
  // "Synced · Fri 18" — a stale day dressed as a fresh one. Past yesterday,
  // the strip says so instead, plus one calm line pointing at the likely
  // cause (another app/device not sharing with Apple Health). A fresh day
  // (today/yesterday) keeps the quiet state, unchanged.
  const fresh = syncFreshnessLabel(lastDay) ?? 'recently';
  const stale = isSyncStale(lastDay);
  return (
    <div className={[styles.strip, styles.synced, className].filter(Boolean).join(' ')}>
      <span className={styles.glyph}>
        <HeartGlyph />
      </span>
      <span className={styles.txt}>
        <span className={styles.lbl}>Apple Health</span>
        <span className={styles.hint}>
          {refreshing ? 'Syncing…' : stale ? `Last synced on ${fresh}` : `Synced · ${fresh}`}
        </span>
        {!refreshing && stale && (
          <span className={styles.staleHint}>Check that your other apps still share with Apple Health.</span>
        )}
      </span>
      <button
        type="button"
        className={styles.refresh}
        onClick={refresh}
        disabled={refreshing}
        aria-label="Refresh Apple Health sync"
      >
        <span className={refreshing ? styles.spin : undefined}>
          <RefreshGlyph />
        </span>
      </button>
    </div>
  );
}
